import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddSubAccountModal } from "./AddSubAccountModal";
import { toast } from "@shared/hooks/use-toast";
import { Eye, Plus, Users } from "lucide-react";
import { formatTerritoryAddress } from "@shared/utils/address";

interface SubAccountTabProps {
  subAccountData: any;
  consumerId: string;
  subCategory: string;
  contractData: any;
}

const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case "ACTIVE":
      return "bg-green-100 text-green-800";
    case "INACTIVE":
      return "bg-gray-100 text-gray-800";
    case "SUSPENDED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getOutstandingColor = (amount: number) =>
  (amount || 0) > 0 ? "text-red-600 font-semibold" : "text-green-600 font-semibold";

const SubAccountTab: React.FC<SubAccountTabProps> = ({
  subAccountData,
  consumerId,
  subCategory,
  contractData,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleAddAccount = (consumer: any) => {
    console.log("Adding sub-account:", consumer);
    toast({
      title: "Sub-account linked",
      description: `Account ${consumer.consumerNo} has been successfully added as a sub-account.`,
      variant: "default",
    });
    setIsAddModalOpen(false);
  };

  // Transform real data
  const transformedData = useMemo(() => {
    const results = subAccountData?.results || [];
    return results.map((item: any) => ({
      id: item.id,
      accountNo: item.consumerNo,
      accountName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      serviceAddress: formatTerritoryAddress(item.territoryData || item.territory_data, "service") || item.serviceAddress || "N/A",
      status: item.statusDisplay || "N/A",
      outstanding: item.outstandingBalance || 0,
      createdOn: item.createdDate || "N/A",
    }));
  }, [subAccountData]);

  // Client-side search over transformed data
  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;
    const q = searchTerm.toLowerCase();
    return transformedData.filter(
      (item: any) =>
        item.accountNo.toLowerCase().includes(q) ||
        item.accountName.toLowerCase().includes(q),
    );
  }, [transformedData, searchTerm]);

  // Client-side pagination slice
  const paginatedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, page]);

  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "accountNo",
        header: "Account No",
      },
      { key: "accountName", header: "Account Name" },
      {
        key: "serviceAddress",
        header: "Service Address",
      },
      {
        key: "status",
        header: "Status",
        type: "custom",
        render: (value) => (
          <Badge className={getStatusColor(value as string)}>{value}</Badge>
        ),
      },
      {
        key: "outstanding",
        header: "Outstanding",
      },
      { key: "createdOn", header: "Created On", type: "text" },
    ],
    [navigate],
  );

  const actions: TableAction<any>[] = useMemo(
    () => [
      {
        label: "View Details",
        onClick: (item) => navigate(`/cx/consumer-management/${item.accountNo}`),
        icon: <Eye className="h-4 w-4 mr-2" />,
      },
    ],
    [navigate],
  );

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems],
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold">Sub Accounts</h2>
        </div>
        <Button variant="default" onClick={() => setIsAddModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Sub Account
        </Button>
      </div>

      <AddSubAccountModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddAccount}
        consumerId={consumerId}
        subCategory={subCategory}
        contractData={contractData}
      />

      <GenericTable
        data={paginatedData}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by Account No, Account Name..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={setPage}
        actions={actions}
        actionsType="icons"
      />
    </div>
  );
};

export default SubAccountTab;
